import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getPromo, savePromo } from "../services/promoService.firebase";
import { listActiveProducts } from "../services/publicProductService";

/* ---------- Helpers ---------- */
const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.round(n) : def;
};
const toIsoOrNull = (v) => (v ? new Date(v).toISOString() : null);
const toLocalInput = (iso) => (iso ? String(iso).slice(0, 16) : "");
const isHttpUrl = (s = "") => /^https?:\/\//i.test(String(s).trim());

const DEFAULT_FORM = {
  title: "",
  subtitle: "",
  ctaLabel: "Voir les offres",
  ctaHref: "/catalogue",
  imageUrl: "",
  theme: "light", // 'light' | 'dark'
  gradientA: "#2d6cdf",
  gradientB: "#7b3fe4",
  productIds: [],
  limitProducts: 10,
  startsAt: "",
  endsAt: "",
  isEnabled: true,
  pinned: false,
  priority: 0,
  // ✅ emplacements
  placements: [],
};

export default function AdminPromoEditor({ isNew }) {
  const { id } = useParams();
  const navigate = useNavigate();

  const [form, setForm] = useState(DEFAULT_FORM);
  const [allProducts, setAllProducts] = useState([]);
  const [prodQuery, setProdQuery] = useState("");
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  /* ---------- Charger produits pour sélection ---------- */
  useEffect(() => {
    (async () => {
      try {
        const res = await listActiveProducts({ pageSize: 300 });
        setAllProducts(res?.items || []);
      } catch {
        // noop
      }
    })();
  }, []);

  /* ---------- Charger promo si édition ---------- */
  useEffect(() => {
    if (isNew) return;
    (async () => {
      setLoading(true);
      try {
        const p = await getPromo(id);
        setForm({
          title: p.title || "",
          subtitle: p.subtitle || "",
          ctaLabel: p.ctaLabel || "Voir les offres",
          ctaHref: p.ctaHref || "/catalogue",
          imageUrl: p.imageUrl || "",
          theme: p.theme || "light",
          gradientA: p.gradient?.[0] || "#2d6cdf",
          gradientB: p.gradient?.[1] || "#7b3fe4",
          productIds: Array.isArray(p.productIds) ? p.productIds : [],
          limitProducts: p.limitProducts ?? 10,
          startsAt: toLocalInput(p.startsAt),
          endsAt: toLocalInput(p.endsAt),
          isEnabled: !!p.isEnabled,
          pinned: !!p.pinned,
          priority: p.priority ?? 0,
          // ✅ hydrate placements
          placements: Array.isArray(p.placements) ? p.placements : [],
        });
      } catch (e) {
        setErr(e.message || "Chargement impossible");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isNew]);

  const update = useCallback((k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
  }, []);
  const toggle = useCallback((k) => {
    setForm((f) => ({ ...f, [k]: !f[k] }));
  }, []);

  /* ---------- Filtrage produits (petit debounce) ---------- */
  const [qDebounced, setQDebounced] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setQDebounced(prodQuery.trim().toLowerCase()), 220);
    return () => clearTimeout(t);
  }, [prodQuery]);

  const filteredProducts = useMemo(() => {
    const q = qDebounced;
    if (!q) return allProducts;
    return allProducts.filter((p) => {
      const hay = `${p.title || p.name || ""} ${(p.brandName || "")} ${p.categoryName || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [allProducts, qDebounced]);

  /* ---------- Détails sélection ---------- */
  const limit = toInt(form.limitProducts, 10);
  const selectedCount = form.productIds.length;
  const remainingSlots = Math.max(0, limit - selectedCount);
  const isAtLimit = selectedCount >= limit;

  const selectedProducts = useMemo(() => {
    const map = new Map(allProducts.map((p) => [p.id || p.slug, p]));
    return form.productIds.map((pid) => map.get(pid)).filter(Boolean);
  }, [form.productIds, allProducts]);

  /* ---------- Validation ---------- */
  const validation = useMemo(() => {
    const errors = {};
    if (!form.title.trim()) errors.title = "Le titre est requis.";
    if (!form.ctaHref.trim()) errors.ctaHref = "Le lien du CTA est requis.";
    if (!form.ctaHref.startsWith("/")) {
      if (!isHttpUrl(form.ctaHref)) errors.ctaHref = "Utilise un chemin interne (/...) ou une URL http(s).";
    }
    if (form.imageUrl && !isHttpUrl(form.imageUrl)) {
      errors.imageUrl = "L'image doit être une URL http(s).";
    }
    if (form.productIds.length === 0) errors.productIds = "Sélectionne au moins un produit.";
    if (form.startsAt && form.endsAt) {
      const d1 = new Date(form.startsAt).getTime();
      const d2 = new Date(form.endsAt).getTime();
      if (Number.isFinite(d1) && Number.isFinite(d2) && d2 < d1) {
        errors.dates = "La date de fin doit être postérieure au début.";
      }
    }
    if (limit < 1) errors.limitProducts = "Nombre maximum invalide (min 1).";
    return { ok: Object.keys(errors).length === 0, errors };
  }, [form, limit]);

  /* ---------- Soumission ---------- */
  const onSave = async (e) => {
    e.preventDefault();
    setErr("");

    if (!validation.ok) {
      const e1 =
        validation.errors.title ||
        validation.errors.ctaHref ||
        validation.errors.imageUrl ||
        validation.errors.productIds ||
        validation.errors.dates ||
        validation.errors.limitProducts ||
        "Le formulaire contient des erreurs.";
      setErr(e1);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        subtitle: form.subtitle?.trim() || "",
        ctaLabel: form.ctaLabel?.trim() || "Voir les offres",
        ctaHref: form.ctaHref?.trim() || "/catalogue",
        imageUrl: form.imageUrl?.trim() || "",
        theme: form.theme,
        gradient: [form.gradientA, form.gradientB],
        productIds: form.productIds.slice(0, limit),
        limitProducts: limit,
        startsAt: toIsoOrNull(form.startsAt),
        endsAt: toIsoOrNull(form.endsAt),
        isEnabled: !!form.isEnabled,
        pinned: !!form.pinned,
        priority: toInt(form.priority, 0),
        // ✅ envoie les placements sélectionnés
        placements: Array.isArray(form.placements) ? form.placements : [],
      };

      await savePromo(payload, isNew ? undefined : id);
      navigate("/admin/promos");
    } catch (e2) {
      setErr(e2.message || "Sauvegarde impossible");
    } finally {
      setSaving(false);
    }
  };

  /* ---------- Sélection produit + outils ---------- */
  const isSelected = (pid) => form.productIds.includes(pid);

  const removeProduct = (pid) => {
    setForm((f) => ({ ...f, productIds: f.productIds.filter((x) => x !== pid) }));
  };

  const toggleProduct = (pid) => {
    setForm((f) => {
      const exists = f.productIds.includes(pid);
      if (exists) return { ...f, productIds: f.productIds.filter((x) => x !== pid) };
      if (f.productIds.length >= limit) return f;
      return { ...f, productIds: [...f.productIds, pid] };
    });
  };

  const moveSel = (pid, dir /* -1 | +1 */) => {
    setForm((f) => {
      const idx = f.productIds.indexOf(pid);
      if (idx < 0) return f;
      const next = [...f.productIds];
      const swapWith = idx + dir;
      if (swapWith < 0 || swapWith >= next.length) return f;
      [next[idx], next[swapWith]] = [next[swapWith], next[idx]];
      return { ...f, productIds: next };
    });
  };

  /* ---------- Aperçu bannière ---------- */
  const bannerStyle = useMemo(() => {
    const g = `linear-gradient(135deg, ${form.gradientA}, ${form.gradientB})`;
    return {
      background: g,
      color: form.theme === "dark" ? "#f8f9fa" : "#111",
      borderRadius: 12,
      overflow: "hidden",
    };
  }, [form.gradientA, form.gradientB, form.theme]);

  const ctaBtnClass = form.theme === "dark" ? "btn-light" : "btn-dark";

  return (
    <div className="container py-4" style={{ maxWidth: 1080 }}>
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
        <h4 className="m-0">{isNew ? "Nouvelle promotion" : "Éditer la promotion"}</h4>
        <div className="d-flex align-items-center gap-2 small text-muted">
          {form.isEnabled ? (
            <span className="badge text-bg-success">Active</span>
          ) : (
            <span className="badge text-bg-secondary">Inactive</span>
          )}
          {form.pinned && <span className="badge text-bg-warning text-dark">Épinglée</span>}
        </div>
      </div>

      {err && <div className="alert alert-danger">{err}</div>}

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border" />
        </div>
      ) : (
        <form onSubmit={onSave}>
          <div className="row g-4">
            {/* Col gauche : formulaire principal */}
            <div className="col-12 col-lg-7">
              <div className="card shadow-sm">
                <div className="card-body">
                  <h6 className="mb-3">Contenu</h6>

                  <div className="row g-3">
                    <div className="col-12">
                      <label className="form-label">Titre *</label>
                      <input
                        className={`form-control ${validation.errors.title ? "is-invalid" : ""}`}
                        value={form.title}
                        onChange={(e) => update("title", e.target.value)}
                        required
                      />
                      {validation.errors.title && (
                        <div className="invalid-feedback">{validation.errors.title}</div>
                      )}
                    </div>

                    <div className="col-12">
                      <label className="form-label">Sous-titre</label>
                      <input
                        className="form-control"
                        value={form.subtitle}
                        onChange={(e) => update("subtitle", e.target.value)}
                        placeholder="Texte complémentaire (facultatif)"
                      />
                    </div>

                    <div className="col-6">
                      <label className="form-label">CTA Label</label>
                      <input
                        className="form-control"
                        value={form.ctaLabel}
                        onChange={(e) => update("ctaLabel", e.target.value)}
                      />
                    </div>
                    <div className="col-6">
                      <label className="form-label">CTA Lien *</label>
                      <input
                        className={`form-control ${validation.errors.ctaHref ? "is-invalid" : ""}`}
                        value={form.ctaHref}
                        onChange={(e) => update("ctaHref", e.target.value)}
                        placeholder="/catalogue ou https://exemple.com/..."
                      />
                      {validation.errors.ctaHref && (
                        <div className="invalid-feedback">{validation.errors.ctaHref}</div>
                      )}
                    </div>

                    <div className="col-12">
                      <label className="form-label">Image (URL)</label>
                      <input
                        className={`form-control ${validation.errors.imageUrl ? "is-invalid" : ""}`}
                        value={form.imageUrl}
                        onChange={(e) => update("imageUrl", e.target.value)}
                        placeholder="https://…"
                      />
                      {validation.errors.imageUrl && (
                        <div className="invalid-feedback">{validation.errors.imageUrl}</div>
                      )}
                      {form.imageUrl && (
                        <div className="mt-2">
                          <img
                            src={form.imageUrl}
                            alt="preview"
                            style={{ maxHeight: 160, borderRadius: 8 }}
                          />
                        </div>
                      )}
                      <div className="form-text">PNG/JPG. Idéalement paysage (≥1200×500).</div>
                    </div>
                  </div>

                  <hr className="my-4" />

                  <h6 className="mb-3">Apparence</h6>
                  <div className="row g-3">
                    <div className="col-6 col-md-4">
                      <label className="form-label">Thème</label>
                      <select
                        className="form-select"
                        value={form.theme}
                        onChange={(e) => update("theme", e.target.value)}
                      >
                        <option value="light">Clair</option>
                        <option value="dark">Sombre</option>
                      </select>
                    </div>
                    <div className="col-6 col-md-4">
                      <label className="form-label">Dégradé A</label>
                      <input
                        type="color"
                        className="form-control form-control-color"
                        value={form.gradientA}
                        onChange={(e) => update("gradientA", e.target.value)}
                      />
                    </div>
                    <div className="col-6 col-md-4">
                      <label className="form-label">Dégradé B</label>
                      <input
                        type="color"
                        className="form-control form-control-color"
                        value={form.gradientB}
                        onChange={(e) => update("gradientB", e.target.value)}
                      />
                    </div>
                    <div className="col-6 col-md-4">
                      <label className="form-label">Nb max produits</label>
                      <input
                        type="number"
                        min={1}
                        className={`form-control ${validation.errors.limitProducts ? "is-invalid" : ""}`}
                        value={form.limitProducts}
                        onChange={(e) => update("limitProducts", e.target.value)}
                      />
                      {validation.errors.limitProducts && (
                        <div className="invalid-feedback">{validation.errors.limitProducts}</div>
                      )}
                    </div>
                    <div className="col-6 col-md-4">
                      <label className="form-label">Priorité</label>
                      <input
                        type="number"
                        className="form-control"
                        value={form.priority}
                        onChange={(e) => update("priority", e.target.value)}
                      />
                      <div className="form-text">Plus grand = plus prioritaire</div>
                    </div>
                  </div>

                  <hr className="my-4" />

                  {/* ✅ Emplacements */}
                  <div className="col-12">
                    <label className="form-label">Emplacements (multi)</label>
                    <select
                      multiple
                      className="form-select"
                      value={form.placements}
                      onChange={(e) =>
                        setForm(f => ({
                          ...f,
                          placements: Array.from(e.target.selectedOptions).map(o => o.value),
                        }))
                      }
                      size={5}
                    >
                      <option value="home_top">Accueil — Bandeau haut</option>
                      <option value="home_mid">Accueil — Milieu</option>
                      <option value="home_bottom">Accueil — Bas</option>
                      <option value="category_page">Page Catégorie</option>
                      <option value="cart">Panier</option>
                    </select>
                    <div className="form-text">Tu peux choisir plusieurs emplacements.</div>
                  </div>

                  <hr className="my-4" />

                  <h6 className="mb-3">Période</h6>
                  <div className="row g-3">
                    <div className="col-6 col-md-4">
                      <label className="form-label">Début</label>
                      <input
                        type="datetime-local"
                        className="form-control"
                        value={form.startsAt}
                        onChange={(e) => update("startsAt", e.target.value)}
                      />
                    </div>
                    <div className="col-6 col-md-4">
                      <label className="form-label">Fin</label>
                      <input
                        type="datetime-local"
                        className={`form-control ${validation.errors.dates ? "is-invalid" : ""}`}
                        value={form.endsAt}
                        onChange={(e) => update("endsAt", e.target.value)}
                      />
                      {validation.errors.dates && (
                        <div className="invalid-feedback">{validation.errors.dates}</div>
                      )}
                    </div>
                    <div className="col-12 col-md-4 d-flex align-items-center gap-3 mt-2">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="enabled"
                          checked={form.isEnabled}
                          onChange={() => toggle("isEnabled")}
                        />
                        <label className="form-check-label" htmlFor="enabled">
                          Active
                        </label>
                      </div>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="pinned"
                          checked={form.pinned}
                          onChange={() => toggle("pinned")}
                        />
                        <label className="form-check-label" htmlFor="pinned">
                          Épinglée
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="d-flex gap-2 mt-3">
                <button
                  className="btn btn-primary"
                  disabled={saving || !validation.ok}
                  title={!validation.ok ? "Corrige les erreurs avant d’enregistrer" : "Enregistrer"}
                >
                  {saving ? "Enregistrement…" : "Enregistrer"}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => navigate("/admin/promos")}
                >
                  Annuler
                </button>
              </div>
            </div>

            {/* Col droite : aperçu + sélection produits */}
            <div className="col-12 col-lg-5">
              {/* Aperçu bannière */}
              <div className="card shadow-sm mb-3">
                <div className="card-body">
                  <h6 className="mb-3">Aperçu bannière</h6>
                  <div style={bannerStyle} className="p-3 p-md-4">
                    <div className="row g-3 align-items-center">
                      <div className="col-12 col-md-7">
                        <h5 className="mb-1">{form.title || "Titre de la promo"}</h5>
                        {form.subtitle && <p className="mb-2">{form.subtitle}</p>}
                        <a
                          href={form.ctaHref || "#"}
                          className={`btn ${ctaBtnClass} btn-sm`}
                          onClick={(e) => e.preventDefault()}
                        >
                          {form.ctaLabel || "Voir les offres"}
                        </a>
                      </div>
                      <div className="col-12 col-md-5 text-center">
                        {form.imageUrl ? (
                          <img
                            src={form.imageUrl}
                            alt="visuel"
                            className="img-fluid rounded"
                            style={{ maxHeight: 180, objectFit: "cover" }}
                          />
                        ) : (
                          <div className="bg-light rounded w-100" style={{ height: 120, opacity: 0.35 }} />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sélecteur de produits */}
              <div className="card shadow-sm">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="m-0">Produits en promotion</h6>
                    <span className={`badge ${isAtLimit ? "text-bg-success" : "text-bg-secondary"}`}>
                      {selectedCount}/{limit}
                    </span>
                  </div>

                  {/* Chips sélection */}
                  {selectedProducts.length > 0 && (
                    <div className="mb-2 d-flex flex-wrap gap-2">
                      {selectedProducts.map((p) => {
                        const pid = p?.id || p?.slug;
                        return (
                          <div key={`chip-${pid}`} className="badge text-bg-light border d-flex align-items-center gap-2 p-2">
                            <span className="small">{p?.title || "Produit"}</span>
                            <div className="btn-group btn-group-sm" role="group" aria-label="Réordonner">
                              <button type="button" className="btn btn-outline-secondary" onClick={() => moveSel(pid, -1)} title="Monter">↑</button>
                              <button type="button" className="btn btn-outline-secondary" onClick={() => moveSel(pid, +1)} title="Descendre">↓</button>
                            </div>
                            <button
                              type="button"
                              className="btn btn-outline-danger btn-sm ms-1"
                              onClick={() => removeProduct(pid)}
                              title="Retirer"
                            >
                              ×
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <div className="mb-2">
                    <input
                      className="form-control"
                      placeholder="Filtrer les produits (nom, marque, catégorie)…"
                      value={prodQuery}
                      onChange={(e) => setProdQuery(e.target.value)}
                    />
                  </div>

                  {validation.errors.productIds && (
                    <div className="alert alert-warning py-2">{validation.errors.productIds}</div>
                  )}

                  <div className="border rounded" style={{ maxHeight: 300, overflow: "auto" }}>
                    {filteredProducts.length === 0 ? (
                      <div className="p-3 text-muted small">Aucun produit.</div>
                    ) : (
                      <ul className="list-group list-group-flush">
                        {filteredProducts.map((p) => {
                          const pid = p.id || p.slug;
                          const checked = isSelected(pid);
                          const price = Math.round(Number(p.price || 0)).toLocaleString("fr-FR");
                          const disableAdd = !checked && isAtLimit;
                          return (
                            <li
                              key={pid}
                              className="list-group-item d-flex align-items-center justify-content-between"
                              style={{ cursor: "pointer", opacity: disableAdd ? 0.6 : 1 }}
                              onClick={() => (disableAdd ? null : toggleProduct(pid))}
                              // aria-disabled={disableAdd}
                            >
                              <div className="d-flex align-items-center gap-2">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={checked}
                                  disabled={disableAdd && !checked}
                                  onChange={() => (disableAdd ? null : toggleProduct(pid))}
                                  onClick={(e) => e.stopPropagation()}
                                />
                                {p.images?.[0] ? (
                                  <img
                                    src={p.images[0]}
                                    alt={p.title}
                                    style={{ width: 40, height: 40, objectFit: "cover", borderRadius: 6 }}
                                  />
                                ) : (
                                  <div className="bg-light rounded" style={{ width: 40, height: 40 }} />
                                )}
                                <div className="d-flex flex-column">
                                  <span className="fw-semibold small">{p.title || "Produit"}</span>
                                  <span className="text-muted small">{price} XOF</span>
                                </div>
                              </div>
                              {checked && <span className="badge text-bg-primary">Sélectionné</span>}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>

                  <div className="small text-muted mt-2">
                    {isAtLimit
                      ? "Limite atteinte. Retirez un article pour en ajouter un autre."
                      : `Encore ${remainingSlots} article(s) disponible(s) avant d’atteindre la limite.`}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      )}
    </div>
  );
}
